import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ChatState } from '../Context/ChatProvider';
import { readStoredUser } from '../Shell/Workspace';
import { t } from '../i18n';

// One place for the GQ Assistant conversations, so the full screen and the floating chat share them.
// Same storage key/shape as before so existing conversations survive: [{ name, content: [{ question, data, note?, failed? }] }]
const KEY = 'GPT_data';
const load = () => {
  try {
    const d = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(d) ? d.filter((c) => c && c.content && c.content.some((m) => m.data)) : [];
  } catch (e) {
    return [];
  }
};

// server messages with a number in them are translated through a template
const friendly = (m) => { const lim = /today's limit of (\d+)/.exec(m); return lim ? t("You reached today's limit of {{n}} questions. It resets tomorrow.", { n: lim[1] }) : t(m); };

const Ctx = createContext(null);
export const useAssistant = () => useContext(Ctx);

export function AssistantProvider({ children }) {
  const { address } = ChatState();
  const me = useMemo(readStoredUser, []);
  const [convs, setConvs] = useState(load);
  const [active, setActiveState] = useState(() => Math.max(0, load().length - 1));
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null); // { used, daily }
  const [allowance, setAllowance] = useState(null); // my monthly QIRA amount: { enabled, unlimited, monthlyUsd, spentUsd, remainingUsd, percent, tokens, estimatedRemainingTokens }
  const [spend, setSpend] = useState(null); // administrators only: { spentUsd, capUsd, percent }
  const [unread, setUnread] = useState(0); // answers that arrived while nobody was looking at the assistant
  const convsRef = useRef(convs); convsRef.current = convs;
  const activeRef = useRef(active); activeRef.current = active;
  const busyRef = useRef(false);
  const watching = useRef(0); // how many views (screen / open floating chat) are showing the assistant right now

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(convs)); } catch (e) { /* storage full or unavailable */ }
  }, [convs]);

  const loadStatus = useCallback(() => axios.get(`${address}/api/gpt/status`).then((r) => { setQuota(r.data.quota); setAllowance(r.data.allowance || null); setSpend(r.data.admin || null); }).catch(() => {}), [address]);
  useEffect(() => { if (me) loadStatus(); }, [me, loadStatus]);

  const setActive = useCallback((i) => { activeRef.current = i; setActiveState(i); }, []);
  const newChat = useCallback(() => {
    const list = [...convsRef.current, { name: t("New chat"), content: [] }];
    convsRef.current = list; setConvs(list); setActive(list.length - 1);
  }, [setActive]);
  const remove = useCallback((i) => {
    setConvs((c) => c.filter((_, k) => k !== i));
    setActive(Math.max(0, activeRef.current >= i ? activeRef.current - 1 : activeRef.current));
  }, [setActive]);
  /** a view that shows the assistant tells it, so answers arriving meanwhile are not counted as unread */
  const watch = useCallback((on) => { watching.current = Math.max(0, watching.current + (on ? 1 : -1)); if (on) setUnread(0); }, []);

  const send = useCallback(async (raw) => {
    const msg = String(raw == null ? '' : raw).trim();
    if (!msg || busyRef.current || !me) return;
    let idx = activeRef.current;
    let list = convsRef.current;
    if (!list[idx]) {
      list = [...list, { name: msg.slice(0, 28), content: [] }];
      idx = list.length - 1;
      setActive(idx);
    }
    list = list.map((c, i) => (i === idx ? { ...c, name: c.content.length ? c.name : msg.slice(0, 28), content: [...c.content, { question: true, data: msg }] } : c));
    convsRef.current = list; setConvs(list);
    busyRef.current = true; setBusy(true);
    let answer; let note = null; let failed = false;
    // the last messages of this conversation go along so the answer has context
    const history = (list[idx].content || []).slice(0, -1).filter((m) => m.data && !m.failed).slice(-6).map((m) => ({ role: m.question ? 'user' : 'assistant', content: String(m.data).slice(0, 1500) }));
    try {
      const { data } = await axios.post(`${address}/api/gpt/question`, { user_logged: me._id, msg, history });
      answer = data && data.choices ? data.choices[0].message.content : typeof data === 'string' ? data : t("The assistant did not return an answer.");
      if (data && data.redacted) note = `${data.redacted} ${t("sensitive items were hidden before sending")}`;
      if (data && data.quota) setQuota(data.quota);
      loadStatus(); // the allowance moves with every answer
    } catch (e) {
      failed = true;
      const m = e.response && e.response.data && e.response.data.message;
      answer = m ? friendly(m) : t("Sorry, I could not reach the assistant. Please try again.");
      loadStatus();
    }
    setConvs((c) => c.map((x, i) => (i === idx ? { ...x, content: [...x.content, { question: false, data: answer, note, failed }] } : x)));
    busyRef.current = false; setBusy(false);
    if (!watching.current) setUnread((n) => n + 1);
  }, [address, me, loadStatus, setActive]);

  const value = { convs, active, current: convs[active], busy, quota, allowance, spend, unread, setActive, newChat, remove, send, watch, loadStatus, outOfQuota: !!(quota && quota.daily > 0 && quota.used >= quota.daily) || !!(allowance && !allowance.unlimited && (!allowance.enabled || (allowance.percent !== null && allowance.percent >= 100))) };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
